/**
 * Performance-only wrapper for FocusAIChatbot.
 *
 * The chatbot module weighs ~78 KB gzipped (react-markdown + the entire
 * micromark/mdast/unified/hast tree). Most sessions never open it. Previously
 * it was lazy() but mounted globally in App.tsx, which meant React fetched
 * the chunk on every cold pageview anyway.
 *
 * This launcher renders a visually-identical pill stub. The chatbot chunk is
 * downloaded only after the user actually triggers it (click, ⌘K, or the
 * existing `focus-ai-open` window event). Once loaded, the launcher unmounts
 * its stub and mounts the real component. After mount, we dispatch
 * `focus-ai-open` so the panel opens on the same gesture that loaded it.
 *
 * No business logic lives here. No props are introduced on FocusAIChatbot.
 * UX is intended to be indistinguishable from the previous behavior, except
 * that the very first open has a brief loading spinner inside the pill while
 * the chunk hydrates.
 */
import React, { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { Loader2, PanelLeft } from "lucide-react";
import { lazyWithRetry } from "@/lib/lazyWithRetry";
import { cn } from "@/lib/utils";
import { ErrorBoundary } from "@/components/ErrorBoundary";

const FocusAIChatbot = lazyWithRetry(() => import("@/components/FocusAIChatbot"));

const FocusAIChatbotLauncher: React.FC = () => {
  const [shouldLoad, setShouldLoad] = useState(false);
  const [isHydrating, setIsHydrating] = useState(false);
  // Track whether the chatbot module has actually mounted so we can hide our stub
  const [hasMounted, setHasMounted] = useState(false);
  const triggeredRef = useRef(false);

  const trigger = useCallback(() => {
    if (triggeredRef.current) {
      // Already loaded (or loading) - just re-dispatch open so the chatbot reacts
      window.dispatchEvent(new CustomEvent("focus-ai-open"));
      return;
    }
    triggeredRef.current = true;
    setIsHydrating(true);
    setShouldLoad(true);
  }, []);

  // Listen for global open requests (HelpCenterSheet, deep-links, etc.)
  useEffect(() => {
    const handler = () => trigger();
    window.addEventListener("focus-ai-open", handler);
    return () => window.removeEventListener("focus-ai-open", handler);
  }, [trigger]);

  // Mirror the chatbot's ⌘K / Ctrl+K shortcut so first-press also loads it.
  useEffect(() => {
    if (triggeredRef.current) return;
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        trigger();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [trigger]);

  // Once the lazy component has actually mounted, dispatch the open event so
  // the panel opens on the same gesture that triggered the load.
  const handleMounted = useCallback(() => {
    setHasMounted(true);
    setIsHydrating(false);
    // Defer to next tick so the chatbot's own listener is attached.
    requestAnimationFrame(() => {
      window.dispatchEvent(new CustomEvent("focus-ai-open"));
    });
  }, []);

  return (
    <>
      {/* Stub icon button - only rendered until the real chatbot has mounted */}
      {!hasMounted && (
        <button
          type="button"
          onClick={trigger}
          aria-label="Open Focus AI"
          className={cn(
            "fixed z-[60] inline-flex h-9 w-9 items-center justify-center rounded-md border border-border bg-background/95 backdrop-blur shadow-sm transition-all duration-200 hover:bg-muted hover:shadow-md",
            // Bottom-left everywhere: top-left covered the Focus logo on several headers.
            "bottom-4 left-4"
          )}
        >
          {isHydrating ? (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          ) : (
            <PanelLeft className="h-4 w-4 text-foreground" strokeWidth={1.75} />
          )}
        </button>
      )}

      {shouldLoad && (
        <ErrorBoundary scope="focus-ai-chatbot" fallback={() => null}>
          <Suspense fallback={null}>
            <ChatbotMountSignal onMount={handleMounted} />
            <FocusAIChatbot />
          </Suspense>
        </ErrorBoundary>
      )}
    </>
  );
};

/**
 * Tiny effect-only component that fires onMount once the lazy chunk resolves
 * and the Suspense boundary commits. Lives next to FocusAIChatbot so it shares
 * the same Suspense boundary.
 */
const ChatbotMountSignal: React.FC<{ onMount: () => void }> = ({ onMount }) => {
  useEffect(() => {
    onMount();
  }, [onMount]);
  return null;
};

export default FocusAIChatbotLauncher;
